import { useCallback, useState, startTransition } from "react";
import { useToast } from "@/hooks/useToast";
import { useAuthStore, useThreadStore } from "@/store";
import { supabase } from "@/lib/supabase";
import { useThreadMoveContext } from "@/contexts/ThreadMoveContext";

/**
 * Hook for handling chat movement between projects and Chats section
 */
export function useChatMove({
  workspaces,
  setWorkspaces,
  workspaceThreads,
  setWorkspaceThreads,
}) {
  const { toast } = useToast();
  const [isMoving, setIsMoving] = useState(false);
  const { isThreadMoving, setThreadMoving } = useThreadMoveContext() || {};

  const handleChatMove = useCallback(
    async (thread, targetWorkspaceId, sourceWorkspaceId = null) => {
      const currentSourceId = sourceWorkspaceId || thread.workspace_id;

      // Don't move if already in target workspace
      if (currentSourceId === targetWorkspaceId) return false;

      // Prevent moving if this thread is already being moved
      if (isThreadMoving && isThreadMoving(thread.id)) {
        console.warn(
          `Thread ${thread.id} is already being moved, ignoring duplicate move request`
        );
        return false;
      }

      // Mark thread as being moved
      if (setThreadMoving) {
        setThreadMoving(thread.id, true);
      }
      setIsMoving(true);

      // Get the target workspace for toast message
      const targetWorkspace = targetWorkspaceId
        ? workspaces.find((w) => w.id === targetWorkspaceId)
        : null;
      const targetName = targetWorkspaceId
        ? targetWorkspace?.name || "project"
        : "Chats";

      // Store original state for rollback on error
      const originalWorkspaceThreads = { ...workspaceThreads };
      const originalWorkspaces = [...workspaces];
      const originalThreads = useThreadStore.getState().threads;

      const newDisplayOrder = 0;
      const updatedThread = {
        ...thread,
        workspace_id: targetWorkspaceId,
        display_order: newDisplayOrder,
        metadata: {
          ...thread.metadata,
          workspace_id: targetWorkspaceId,
          moved_to_history_at: targetWorkspaceId
            ? null
            : new Date().toISOString(),
        },
      };

      // OPTIMISTIC UPDATE: Update local state FIRST for instant UI feedback
      startTransition(() => {
        // Update workspace threads state immediately
        setWorkspaceThreads((prev) => {
          const updated = { ...prev };

          // Remove from ALL workspaces first to ensure thread is only in one place
          Object.keys(updated).forEach((workspaceId) => {
            if (updated[workspaceId]) {
              updated[workspaceId] = updated[workspaceId].filter(
                (t) => t.id !== thread.id
              );
            }
          });

          // Add to target and shift existing threads
          if (targetWorkspaceId) {
            const targetList = updated[targetWorkspaceId] || [];
            // Shift all existing threads down by 1
            const shiftedThreads = targetList.map((t) => ({
              ...t,
              display_order: (t.display_order || 0) + 1,
            }));
            // Add the new thread at the top (display_order = 0)
            updated[targetWorkspaceId] = [updatedThread, ...shiftedThreads];
          }

          return updated;
        });

        // Update workspace state - ensure thread is removed from ALL workspaces.
        // useAuthStore.setWorkspaces accepts a plain array only (it ignores
        // function updaters), so compute the next array from `workspaces`.
        const updatedWorkspaces = workspaces.map((ws) => {
          const hasThread = (ws.workspace_threads || []).includes(thread.id);

          // Remove thread from all workspaces except target
          if (hasThread && ws.id !== targetWorkspaceId) {
            return {
              ...ws,
              workspace_threads: (ws.workspace_threads || []).filter(
                (id) => id !== thread.id
              ),
            };
          }

          // Add thread to target workspace if not already there
          if (ws.id === targetWorkspaceId && !hasThread) {
            return {
              ...ws,
              workspace_threads: [...(ws.workspace_threads || []), thread.id],
            };
          }

          return ws;
        });
        setWorkspaces(updatedWorkspaces);

        // Update thread store
        useThreadStore.setState((state) => {
          const updatedThreads = state.threads.map((t) => {
            if (t.id === thread.id) {
              return updatedThread;
            }
            // Shift display_order for existing threads in target workspace
            if (targetWorkspaceId && t.workspace_id === targetWorkspaceId) {
              return {
                ...t,
                display_order: (t.display_order || 0) + 1,
              };
            }
            return t;
          });
          return { threads: updatedThreads };
        });
      });

      // Perform atomic database operation
      try {
        // Get current user ID for the RPC call
        const currentUser = useAuthStore.getState().user;
        const userId = currentUser?.id || thread.user_id;

        // Call the atomic RPC function that handles everything in a single transaction
        // This ensures: removal from all workspaces, addition to target, thread updates, display_order shifts
        const { data: result, error: rpcError } = await supabase.rpc(
          "move_thread_to_workspace_atomic",
          {
            p_thread_id: thread.id,
            p_target_workspace_id: targetWorkspaceId,
            p_display_order: newDisplayOrder,
            p_user_id: userId,
          }
        );

        if (rpcError) {
          throw rpcError;
        }

        // Log the result for debugging
        if (result?.removed_count > 0) {
          console.log(
            `Thread ${thread.id} moved atomically. Removed from ${result.removed_count} workspace(s).`
          );
        }

        // Verify the move was successful by checking the result
        if (result?.success !== true) {
          throw new Error("Move operation did not complete successfully");
        }

        // Show success toast only after database confirms
        toast({
          title: "Success",
          description: `Thread moved to ${targetName}`,
        });

        return true;
      } catch (error) {
        console.error("Error moving thread:", error);

        // Rollback optimistic updates on error - use startTransition for smooth UI update
        startTransition(() => {
          setWorkspaceThreads(originalWorkspaceThreads);
          setWorkspaces(originalWorkspaces);
          useThreadStore.setState({ threads: originalThreads });
        });

        toast({
          title: "Error",
          description:
            "Failed to move thread. It has been restored to its original location.",
          variant: "destructive",
        });

        return false;
      } finally {
        // Remove thread from moving set and reset moving state
        if (setThreadMoving) {
          setThreadMoving(thread.id, false);
        }
        setIsMoving(false);
      }
    },
    [workspaces, setWorkspaces, workspaceThreads, setWorkspaceThreads, toast]
  );

  return {
    handleChatMove,
    isMoving,
  };
}
